import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { userRequest } from '../utils/requestMethods';
import { FiCheckCircle, FiAlertTriangle, FiLoader } from 'react-icons/fi';

const BillingSuccess = () => {
  const query = new URLSearchParams(useLocation().search);
  const subscriptionId = query.get('subscriptionId');
  const { refreshSubscription } = useSubscription();
  const history = useHistory();

  const [status, setStatus] = useState('verifying');
  const pollCountRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!subscriptionId) {
      history.replace('/billing');
      return;
    }

    intervalRef.current = setInterval(async () => {
      try {
        const { data } = await userRequest.get(`/subscriptions/${subscriptionId}`);
        if (data.status === 'active') {
          clearInterval(intervalRef.current);
          setStatus('success');
          refreshSubscription();
        }
      } catch (err) {
        console.error('Payment verification poll error:', err);
      }

      pollCountRef.current += 1;
      if (pollCountRef.current >= 20) {
        clearInterval(intervalRef.current);
        setStatus('timeout');
      }
    }, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [subscriptionId, history, refreshSubscription]);

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10">
      <div className="max-w-md mx-auto bg-white dark:bg-secondary-dark-bg rounded-2xl shadow-lg p-8 text-center">
        {status === 'verifying' && (
          <>
            <FiLoader className="w-16 h-16 text-indigo-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-200 mb-2">
              Verifying Payment
            </h2>
            <p className="text-gray-500">
              Please wait while we confirm your payment. This usually takes a few seconds...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <FiCheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-200 mb-2">
              Payment Successful!
            </h2>
            <p className="text-gray-500 mb-6">
              Your subscription is now active. You have full access to all features.
            </p>
            <button
              onClick={() => history.push('/home')}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-full px-6 py-3 transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
              <FiAlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-200 mb-2">
              Verification in Progress
            </h2>
            <p className="text-gray-500 mb-6">
              Your payment is being processed and will be reflected shortly. You can check back on the billing page.
            </p>
            <button
              onClick={() => history.push('/billing')}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-full px-6 py-3 transition-colors"
            >
              Back to Billing
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSuccess;
